import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { Select } from "@/components/Select";

export interface ListPaginationProps {
    page: number;
    pageSize: number;
    total: number;
    onPage: (page: number) => void;
    onPageSize?: (size: number) => void;
    pageSizeOptions?: number[];
}

export const ListPagination = ({
    page,
    pageSize,
    total,
    onPage,
    onPageSize,
    pageSizeOptions = [20, 50, 100],
}: ListPaginationProps) => {
    const { t } = useTranslation("global");
    const pages =
        pageSize === -1 ? 1 : Math.max(1, Math.ceil(total / pageSize));
    const current = Math.max(1, Math.min(page, pages));
    const numbers = Array.from(
        new Set([1, current - 1, current, current + 1, pages])
    )
        .filter((value) => value >= 1 && value <= pages)
        .sort((a, b) => a - b);
    const start = total
        ? pageSize === -1
            ? 1
            : (current - 1) * pageSize + 1
        : 0;
    const end = pageSize === -1 ? total : Math.min(current * pageSize, total);
    return (
        <nav
            aria-label={t("workspace.list.pagination_label")}
            className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 text-xs"
        >
            <span
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="tabular-nums text-slate-600 dark:text-zinc-300"
            >
                {t("workspace.list.pagination", { start, end, total })}
            </span>
            <div className="flex flex-wrap items-center gap-1.5">
                {onPageSize && (
                    <Select<number>
                        size="sm"
                        ariaLabel={t("workspace.list.page_size_label")}
                        value={pageSize}
                        options={Array.from(
                            new Set([...pageSizeOptions, pageSize])
                        ).map((value) => ({
                            value,
                            label:
                                value === -1
                                    ? t("pagination.all")
                                    : t("workspace.list.page_size", {
                                          count: value,
                                      }),
                        }))}
                        onChange={(size) => onPageSize(Number(size))}
                    />
                )}
                <Button
                    variant="ghost"
                    size="sm"
                    onlyicon
                    icon="chevronLeft"
                    aria-label={t("workspace.list.previous")}
                    disabled={current === 1}
                    onClick={() => onPage(current - 1)}
                />
                {numbers.map((value, index) => (
                    <span
                        key={value}
                        className="inline-flex items-center gap-1.5"
                    >
                        {index > 0 && value > numbers[index - 1] + 1 && (
                            <span aria-hidden="true">…</span>
                        )}
                        <Button
                            variant={current === value ? "primary" : "ghost"}
                            size="sm"
                            aria-label={t("workspace.list.page", {
                                page: value,
                            })}
                            aria-current={
                                current === value ? "page" : undefined
                            }
                            onClick={() => onPage(value)}
                        >
                            {value}
                        </Button>
                    </span>
                ))}
                <Button
                    variant="ghost"
                    size="sm"
                    onlyicon
                    icon="chevronRight"
                    aria-label={t("workspace.list.next")}
                    disabled={current === pages}
                    onClick={() => onPage(current + 1)}
                />
            </div>
        </nav>
    );
};
